import React, { useState, useCallback, useEffect, useContext } from 'react';

import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FilterList, KeyboardArrowDown, Person, SaveAlt, Search } from '@mui/icons-material';
import { toCapitalize } from '../../../lib/helpers/helpers';
import usePatientLists from '../../../lib/hooks/usePatientLists';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { API_HOST } from '../../../lib/constants/constants';
import { PartnerContext, PatientTabsContext } from '../../../lib/contexts';
import { postExport } from '../../../lib/api';
import { DownloadProgressDialog } from '../../../components/DownloadProgressDialog';
import { AddPatientListDialog } from '../../../components/AddPatientListDialog';
import AdminTextField from '../../../components/AdminTextField';
import { ThemeContext } from '../../../lib/contexts/ThemeContext'; // Import ThemeContext
const useStyles = makeStyles((theme) => ({
  screen: {
    width: '100%',
    margin: '0px auto 0px',
    '@media print': {
      display: 'none',
    },
  },
  card: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'center',
    maxWidth: 300,
    marginBottom: '25px',
  },
  iconAdjust: {
    marginBottom: -3,
    marginLeft: 7,
    marginRight: 10,
    color: theme.palette.admin.main,
  },
  label: {
    color: theme.palette.admin.bodyText,
  },
  select: {
    color: theme.palette.admin.main,
    '& .MuiSelect-icon': {
      color: theme.palette.admin.main,
    },
    '& .Mui-focused .MuiInputLabel-root': {
      color: theme.palette.admin.dark,
    },
  },

  inputBase: {
    marginLeft: theme.spacing(1),
    flex: 1,
    width: theme.spacing(100),
  },
  iconButton: {
    padding: 10,
  },
  dropdown: {
    minWidth: 200,
    top: -5,
  },
  exportButton: {
    width: 100,
    height: 48,
    color: theme.palette.admin.main,
    '& span': {
      marginBottom: -2,
    },
    '& svg': {
      marginBottom: 5,
      marginLeft: 0,
      marginRight: 5,
    },
  },
}));

const PatientSearchBar = ({ patientStringFilter, handlePatientStringFilter, handlePatientListFilter, setPage }) => {
  const classes = useStyles();
  const {
    state: { darkMode },
  } = useContext(ThemeContext);
  let { listId } = useParams();
  let navigate = useNavigate();
  const {
    actions: { setLastListId },
  } = useContext(PatientTabsContext);
  const { isLoading, isRefetching, data: dataObject, refetch: refetchInternal } = usePatientLists();
  const { data: patientLists } = dataObject ?? [];

  const [exportMenuOpen, setExportMenuOpen] = useState(null);
  const [isDownloading, setIsDownloading] = useState(null);

  const [patientListFilter, setPatientListFilter] = useState(listId);
  const [coachFilter, setCoachFilter] = useState('');
  const [resultsSorting, setResultsSorting] = useState('desc');
  const [coachList, setCoachList] = useState([]);
  const [newListDialog, setNewListDialog] = useState(false);

  const handlePatientListFilterInternal = useCallback(
    (event) => {
      setPage(0);
      const val = event.target.value;
      setPatientListFilter(val);
      if (patientLists && val) {
        let chosenList = patientLists.filter((list) => list.id == val)[0];
        handlePatientListFilter(val === 'all' ? 'all' : chosenList['patient_ids']);
        //update url
        if (val === 'all') {
          setLastListId(null);
        }
        navigate(val === 'all' ? '../patients/' : '../patients/list/' + chosenList.id, { replace: true });
      }
    },
    [patientLists],
  );

  useEffect(() => {
    if (listId && patientLists && patientLists?.length) {
      let chosenList = patientLists?.filter((list) => list.id == listId)[0];
      handlePatientListFilter(chosenList['patient_ids']);
    }
  }, [patientLists, listId]);

  const handleExportMenu = useCallback((event) => {
    setExportMenuOpen(event.currentTarget);
  }, []);

  const handleExportMenuClose = useCallback((event) => {
    setExportMenuOpen(null);
  }, []);

  const handleFileExport = useCallback(
    (type, format) => {
      if (type && format) {
        setIsDownloading(type);
        postExport(type, format, listId)
          .then((result) => {
            if (result.status === 200) {
              if (result.data && result.data.email_sent) {
                alert(
                  result.data.message ||
                    'Your download is taking longer than usual, it has been sent to your email instead.',
                );
              } else {
                const data = result.data;
                const prefix =
                  listId && patientLists && patientLists.length
                    ? patientLists.filter((list) => list.id == listId)[0].list_name + '_'
                    : '';
                const fileName = prefix + type + '.' + format;
                const blob = new Blob([format === 'csv' ? data : JSON.stringify(data)], {
                  type: format === 'csv' ? 'text/csv' : 'text/json',
                });
                // Create an anchor element and dispatch a click event on it to trigger a download
                const a = document.createElement('a');
                a.download = fileName;
                a.href = window.URL.createObjectURL(blob);
                const clickEvt = new MouseEvent('click', {
                  view: window,
                  bubbles: true,
                  cancelable: true,
                });
                a.dispatchEvent(clickEvt);
                a.remove();
              }

              /*
          anchor technique
          var element = document.createElement('a');
          element.setAttribute('href', 'data:text/plain;charset=utf-8,' + (format === 'csv' ? text : JSON.stringify(text)));
          element.setAttribute('download', filename);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
          */
            } else {
              alert('Something went wrong, please try again.');
            }

            setIsDownloading(null);
            setExportMenuOpen(null);
          })
          .catch(() => {
            alert('Something went wrong, please try again.');
            setIsDownloading(null);
            setExportMenuOpen(null);
          });
      }
    },
    [listId, patientLists],
  );

  return (
    <Grid
      container
      alignItems="center"
      spacing={0}
      className={classes.screen}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
      >
        <Paper className={classes.card}>
          <IconButton
            type="submit"
            className={classes.iconButton}
            aria-label="search"
            size="large"
          >
            <Search />
          </IconButton>
          <InputBase
            value={patientStringFilter}
            onChange={handlePatientStringFilter}
            onBlur={handlePatientStringFilter}
            className={classes.inputBase}
            placeholder="Search Patients"
            inputProps={{
              'aria-label': 'search patients',
              style: { color: darkMode ? '#ffffff' : '#000000' },
            }}
          />
        </Paper>

        <Paper
          className={classes.card}
          style={{ display: 'none' }}
        >
          <Person />
          <FormControl className={classes.dropdown}>
            <InputLabel id="coach-list-filter-label">Filter by Coach</InputLabel>
            <Select
              labelId="coach-list-filter-label"
              id="coach-list-filter"
              value={coachFilter}
              onChange={setCoachFilter}
            >
              {coachList &&
                coachList.map((list, index) => {
                  return (
                    <MenuItem
                      key={'coachList' + list.id}
                      value={list.id}
                    >
                      {toCapitalize(list.list_name)}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Paper>
        <Grid
          item
          xs={4}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={0}
          >
            <Paper className={classes.card}>
              <Button
                variant={'text'}
                className={classes.exportButton}
                onClick={handleExportMenu}
              >
                <SaveAlt />
                Export
              </Button>
              <Menu
                id="export-menu"
                anchorEl={exportMenuOpen}
                open={Boolean(exportMenuOpen)}
                onClose={handleExportMenuClose}
                anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
              >
                <MenuItem
                  disabled={!patientListFilter || patientListFilter === 'all'}
                  onClick={() => handleFileExport('users_list', 'csv')}
                >
                  Current List - Users [CSV]
                </MenuItem>
                <MenuItem
                  disabled={!patientListFilter || patientListFilter === 'all'}
                  onClick={() => handleFileExport('users_list', 'json')}
                >
                  Current List - Users [JSON]
                </MenuItem>
                <MenuItem
                  disabled={!patientListFilter || patientListFilter === 'all'}
                  onClick={() => handleFileExport('workflows_list', 'csv')}
                >
                  Current List - Workflows [CSV]
                </MenuItem>
                <MenuItem
                  disabled={!patientListFilter || patientListFilter === 'all'}
                  onClick={() => handleFileExport('workflows_list', 'json')}
                >
                  Current List - Workflows [JSON]
                </MenuItem>
                <MenuItem onClick={() => handleFileExport('users', 'csv')}>All Users [CSV]</MenuItem>
                <MenuItem onClick={() => handleFileExport('users', 'json')}>All Users [JSON]</MenuItem>
                <MenuItem onClick={() => handleFileExport('workflows', 'csv')}>All Workflows [CSV]</MenuItem>
                <MenuItem onClick={() => handleFileExport('workflows', 'json')}>All Workflows [JSON]</MenuItem>
              </Menu>
              {isDownloading && <DownloadProgressDialog type={isDownloading} />}
            </Paper>

            <FormControl className={classes.dropdown}>
              <InputLabel
                id="patient-list-filter-label"
                className={classes.label}
              >
                Filter by Patient List
              </InputLabel>
              <Select
                style={{ minWidth: 200 }}
                id="outlined-select"
                label="Filter by Patient List"
                value={patientListFilter}
                onChange={handlePatientListFilterInternal}
              >
                <MenuItem value={'all'}>All Patients</MenuItem>
                {Boolean(patientLists && patientLists.length) &&
                  patientLists.map((list) => (
                    <MenuItem
                      key={'patientLists' + list.id}
                      value={list.id}
                    >
                      {toCapitalize(list.list_name)}
                    </MenuItem>
                  ))}
                <MenuItem onClick={() => setNewListDialog(true)}>Create New List</MenuItem>
              </Select>
            </FormControl>
            {newListDialog && (
              <AddPatientListDialog
                invisible={true}
                refetch={refetchInternal}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        style={{ display: 'none' }}
      >
        <Paper className={classes.search}>
          <FormControl className={classes.dropdown}>
            <InputLabel id="results-list-filter-label">Sort by</InputLabel>
            <Select
              labelId="results-list-filter-label"
              id="results-list-filter"
              value={resultsSorting}
              onChange={setResultsSorting}
            >
              <MenuItem value={'desc'}>Latest</MenuItem>
              <MenuItem value={'asc'}>Earliest</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        <Grid
          item
          xs={3}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={0}
          >
            <Paper className={classes.search}>
              <Button variant={'outlined'}>Export</Button>
            </Paper>

            <Paper className={classes.search}>
              <FilterList />
              <Button variant={'outlined'}>Columns</Button>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PatientSearchBar;
